import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import { useGLTF } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { ktx2Extend } from '../utils/ktx2';
import { assetUrl } from '../services/assets';
import { useWorldStore } from '../state/worldStore';
import { useVenuePlacement } from '../hooks/useVenuePlacement';
import { detectFloor } from '../utils/venueFloor';
import { ROOMS, TOWN_LOCATIONS, ENTRY_VIA_PORTAL } from '../rooms';

// VenueShells — a sited venue's OUTSIDE, standing in the city.
//
// A sited venue occupies a real address, but until now it only existed once you
// were inside it: from the street there was an anchor, a marker, and nothing
// else. That was survivable while venues sat on top of existing buildings — the
// building was the shell. The moment one is moved to an empty lot, the shop
// becomes an invisible hole you walk into, and the city has a doorway to nowhere
// in the middle of it.
//
// SHELL OUTSIDE, INTERIOR INSIDE — and never both. The interior is 14.6MB and
// 388k triangles; rendering it from the street would put the whole thing on
// screen permanently for a building you are walking past. The shell is what the
// city pays for, all the time; the interior is what entering pays for, briefly.
//
// Two ways to get one, in order of preference:
//
//   rooms.js `shell`   an exterior-only GLB — the real answer. Same placement
//                      transform as the interior, so they line up by
//                      construction rather than by tuning.
//   nothing            a MASSING BLOCK at the venue's measured footprint.
//
// The massing block is not a debug box. A venue with no shell is a hole in the
// street, and a plain volume of the right size in the right place is a better
// city than a gap — it is what an architect would put on the model before the
// facade is designed. It is also honest: it reads as unfinished, which it is.
const SHELL_COLOR = '#8d8579';

// The interior GLB placed as an exterior. Only mounted when a room declares
// `shell`, so nothing loads for venues that have not got one.
function ShellModel({ url, scale, footprint, fit, shellY }) {
  const gl = useThree((s) => s.gl);
  const ktx2 = useMemo(() => ktx2Extend(gl), [gl]);
  const { scene } = useGLTF(assetUrl(url), true, true, ktx2);

  // Cloned and fitted the same way MeshInterior fits its own model: centred on
  // its footprint with the floor at y=0, so a shell and the interior it wraps
  // agree about where the ground is without either being hand-nudged.
  const model = useMemo(() => {
    const m = scene.clone(true);

    // STRIP BAKED LIGHTS — belt and braces, matching MeshInterior.
    //
    // The pipeline removes these now, but a shell that leaks one is far worse
    // than an interior that does: an interior's bad lamp blows out one room,
    // while a shell puts it in the OPEN CITY. This store's two arrive at
    // intensity 543 against the ~7.5 the town is lit at, so it overwhelmed the
    // day/night rig entirely and the building stopped responding to the world's
    // own light — which is exactly how it was reported.
    //
    // Kept as a runtime guard because shells are downloaded assets rebuilt by
    // whoever adds a venue, and the cost of missing one is a lighting bug
    // nobody would think to blame on a building's exterior.
    const doomed = [];
    m.traverse((o) => { if (o.isLight) doomed.push(o); });
    for (const l of doomed) l.parent?.remove(l);
    if (doomed.length) console.warn(`[shell] removed ${doomed.length} baked light(s) — rebuild this shell with --shell to strip them at source`);

    m.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(m);
    const size = new THREE.Vector3(); box.getSize(size);
    const centre = new THREE.Vector3(); box.getCenter(centre);
    // MATCH THE INTERIOR'S FORMULA EXACTLY.
    //
    // This used to divide by size.x while MeshInterior divides by the LONGEST
    // horizontal axis — identical whenever x happens to be longest, and silently
    // different whenever it is not. Two components deriving the same number two
    // ways is how a shell and its interior end up at different sizes with both
    // looking individually correct.
    //
    // SIZED FROM AN AUTHORED NUMBER, NOT A MEASUREMENT — the first visitor has
    // no measurement.
    //
    // This used to size against `footprint`, which is worldStore.venueFootprints
    // — recorded by MeshInterior, which only ever runs while you are STANDING
    // INSIDE the room. So on a first-ever visit there was nothing to divide by,
    // the fit fell through to 1, and the shell rendered at the GLB's own scale:
    // this store is authored in metres, so it stood 29.5 units long against the
    // 13.4 it should be. Exactly 2.2x, which is the world's metres-per-unit.
    //
    // It was invisible to us because the footprint is cached in localStorage.
    // Walking in once records it and fixes the shell on THIS machine forever,
    // while every new visitor still gets the oversized building on approach —
    // a bug that cannot be reproduced by the only person looking for it.
    //
    // `fitTo` is the same quantity (longest horizontal axis, in world units)
    // arrived at by measuring the model offline rather than at runtime, so it is
    // there on frame one and identical on every machine. The measured footprint
    // stays as a fallback for a venue that has no authored fit yet.
    const longest = Math.max(size.x, size.z) || 1;
    const target = fit ?? (footprint ? footprint[0] : null);
    const s = target ? target / longest : 1;
    // SAME FLOOR RULE AS THE INTERIOR, or the two disagree about where the
    // ground is and one Y nudge cannot correct both. This store's walls run a
    // quarter-unit below the slab into a foundation, so grounding on the box
    // bottom lifts the building; the interior already ignores that, and a shell
    // that did not would sit a step higher than the room inside it.
    const floorY = detectFloor(m);
    const groundY = floorY ?? box.min.y;
    const g = new THREE.Group();
    g.add(m);
    m.position.set(-centre.x * s, -groundY * s, -centre.z * s);
    m.scale.setScalar(s);
    // ONE COMPARABLE LINE, matching MeshInterior's.
    //
    // The shell and the interior are grounded by two different components, and
    // each has been verified on its own — which is exactly how they ended up
    // disagreeing with one another while both looked correct in isolation. The
    // only way to see a MISMATCH is to print the same measurement from both and
    // read them side by side.
    const source = fit ? 'rooms.js fit' : (footprint ? 'measured footprint' : 'NOTHING — rendering at the GLB\'s own scale, which will be wrong');
    console.log(`[shell] floor -> local y 0 · scale ${s.toFixed(4)} (${target ? target.toFixed(1) : '?'} from ${source} / model ${longest.toFixed(1)})`
      + ` · box y ${box.min.y.toFixed(3)}..${box.max.y.toFixed(3)} · ${floorY == null ? 'NO FLOOR FOUND, grounded on box bottom' : `floor at ${floorY.toFixed(3)}`}`);
    return g;
  }, [scene, footprint, fit]);

  return <primitive object={model} position={[0, shellY || 0, 0]} scale={scale} />;
}

function Shell({ loc }) {
  const roomId = loc.interiorId;
  const room = ROOMS[roomId] || {};
  const { pos, scale, rotY, sited } = useVenuePlacement(roomId);
  const sizes = useWorldStore((s) => s.venueFootprints);
  const enterInterior = useWorldStore((s) => s.enterInterior);
  const base = sizes?.[roomId];
  // The shell's OWN trim, draft first so a nudge shows before it is saved.
  const draft = useWorldStore((st) => st.venuePlaceDraft?.[roomId]);
  const savedShell = useWorldStore((st) => st.venueShellOverrides?.[roomId]);
  const shellY = draft?.shellY ?? savedShell?.y ?? room.shellY ?? 0;
  const shellScale = draft?.shellScale ?? savedShell?.scale ?? 1;

  // THE SAME TRAP, ONE BRANCH DOWN. The massing block is sized purely from the
  // measured footprint, so a sited venue with no shell GLB draws NOTHING at all
  // until somebody has been inside it — and then draws correctly forever on that
  // one machine, exactly like the oversized-shell bug this file just fixed.
  // Grocery has a shell so it is not affected today; the next sited venue will
  // be, and it would present as "my building never appeared".
  useEffect(() => {
    if (sited && !room.shell && !base) {
      console.warn(`[shell] "${roomId}" is sited with no shell GLB and no measured footprint — nothing will be drawn on the street until someone enters it. Give it a rooms.js \`shell\`, or a \`fitTo\` and a shell, rather than relying on the visit.`);
    }
  }, [sited, room.shell, base, roomId]);

  if (!sited || !pos) return null;

  const fp = base ? [base[0] * scale, base[1] * scale, base[2] * scale] : null;

  return (
    <group position={pos} rotation={[0, rotY, 0]}>
      {room.shell ? (
        // `shellFit` first so a shell that will not line up can be corrected on
        // its own, then the room's authored `fitTo` — which is what makes the
        // building the right size for someone who has never been inside it.
        <ShellModel url={room.shell} scale={scale * shellScale} footprint={base} fit={room.shellFit ?? room.fitTo} shellY={shellY} />
      ) : fp ? (
        // Massing block. Centred on its footprint and sitting ON the ground
        // (hence the half-height lift), matching how MeshInterior grounds the
        // interior — so walking in does not drop you through a floor that was
        // never in the same place.
        <mesh
          position={[0, fp[1] / 2, 0]}
          // Doors on: the block is scenery, not a button. See ENTRY_VIA_PORTAL.
          onClick={ENTRY_VIA_PORTAL ? undefined : (e) => { e.stopPropagation(); enterInterior(roomId); }}
          onPointerOver={ENTRY_VIA_PORTAL ? undefined : (e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; }}
          onPointerOut={ENTRY_VIA_PORTAL ? undefined : () => { document.body.style.cursor = 'auto'; }}
          castShadow
          receiveShadow
        >
          <boxGeometry args={[fp[0], fp[1], fp[2]]} />
          <meshStandardMaterial color={SHELL_COLOR} roughness={0.85} metalness={0.05} />
        </mesh>
      ) : null}
    </group>
  );
}

export default function VenueShells() {
  // The venue you are INSIDE has no shell drawn: you would be standing in the
  // middle of a solid block, looking at the inside of its walls, with the real
  // interior z-fighting somewhere in there too. Every OTHER sited venue keeps
  // its shell, because a sited room can see the street and the rest of the town
  // should still be standing on it.
  const interior = useWorldStore((s) => s.interior);
  return (
    <group>
      {TOWN_LOCATIONS.map((loc) => (
        loc.interiorId === interior ? null : <Shell key={loc.key} loc={loc} />
      ))}
    </group>
  );
}
